import React, { useState, useEffect } from 'react';
import { Package, ShoppingCart, FileText, Users, TrendingUp, Loader2 } from 'lucide-react';

import { API_URL, BACKEND_URL } from '../../config/api';
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API}/admin/stats`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-fgs-primary" />
      </div>
    );
  }

  const statCards = [
    { 
      label: 'Produits', 
      value: stats?.products || 0, 
      icon: Package, 
      color: 'blue',
      bgColor: 'bg-blue-500/20',
      textColor: 'text-blue-400'
    },
    { 
      label: 'Commandes', 
      value: stats?.orders?.total || 0, 
      icon: ShoppingCart, 
      color: 'green',
      bgColor: 'bg-green-500/20',
      textColor: 'text-green-400',
      subtext: `${stats?.orders?.pending || 0} en attente`
    },
    { 
      label: 'Devis', 
      value: stats?.quotes?.total || 0, 
      icon: FileText, 
      color: 'purple',
      bgColor: 'bg-purple-500/20',
      textColor: 'text-purple-400',
      subtext: `${stats?.quotes?.pending || 0} en attente`
    },
    { 
      label: 'Clients', 
      value: stats?.users?.clients || 0, 
      icon: Users, 
      color: 'orange',
      bgColor: 'bg-orange-500/20',
      textColor: 'text-orange-400'
    },
  ];

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-500/20 text-yellow-400',
      confirmed: 'bg-blue-500/20 text-blue-400',
      shipped: 'bg-purple-500/20 text-purple-400',
      delivered: 'bg-green-500/20 text-green-400',
      cancelled: 'bg-red-500/20 text-red-400'
    };
    const labels = {
      pending: 'En attente',
      confirmed: 'Confirmée',
      shipped: 'Expédiée',
      delivered: 'Livrée',
      cancelled: 'Annulée'
    };
    return (
      <span className={`px-2 py-0.5 rounded text-xs font-medium ${styles[status] || styles.pending}`}>
        {labels[status] || status}
      </span>
    );
  };

  return (
    <div>
      <h1 className="font-heading text-2xl font-bold text-white mb-6">Tableau de bord</h1>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {statCards.map((stat, i) => (
          <div key={i} className="bg-fgs-surface border border-fgs-border rounded-xl p-5">
            <div className="flex items-center justify-between mb-3">
              <div className={`w-10 h-10 rounded-lg ${stat.bgColor} flex items-center justify-center`}>
                <stat.icon className={`w-5 h-5 ${stat.textColor}`} />
              </div>
            </div>
            <p className="text-2xl font-bold text-white">{stat.value}</p>
            <p className="text-sm text-fgs-text-muted">{stat.label}</p>
            {stat.subtext && (
              <p className={`text-xs ${stat.textColor} mt-1`}>{stat.subtext}</p>
            )}
          </div>
        ))}
      </div>

      {/* Revenue Card */}
      <div className="bg-fgs-surface border border-fgs-border rounded-xl p-6 mb-8">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 rounded-xl bg-fgs-primary/20 flex items-center justify-center">
            <TrendingUp className="w-6 h-6 text-fgs-primary" />
          </div>
          <div>
            <p className="text-sm text-fgs-text-muted">Chiffre d'affaires (livré)</p>
            <p className="text-3xl font-bold text-white font-mono">{(stats?.revenue || 0).toFixed(2)} €</p>
          </div>
        </div>
      </div>

      {/* Recent Orders */}
      <div className="bg-fgs-surface border border-fgs-border rounded-xl p-6">
        <h2 className="font-heading text-lg font-bold text-white mb-4">Commandes récentes</h2>
        
        {stats?.recent_orders?.length > 0 ? (
          <div className="space-y-3">
            {stats.recent_orders.map(order => (
              <div 
                key={order.order_id}
                className="flex items-center justify-between p-3 bg-fgs-bg rounded-lg"
              >
                <div>
                  <p className="text-white font-mono text-sm">{order.order_id}</p>
                  <p className="text-xs text-fgs-text-muted">
                    {new Date(order.created_at).toLocaleDateString('fr-FR')}
                  </p>
                </div>
                <div className="flex items-center gap-4">
                  {getStatusBadge(order.status)}
                  <span className="text-fgs-primary font-mono font-bold">
                    {order.total_amount?.toFixed(2)} €
                  </span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-fgs-text-muted text-center py-8">Aucune commande récente</p>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
